// ** React imports
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// ** Third Party imports
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

// ** Custom imports
import { toaster } from '@saleshandy/design-system';
import { RootState } from '../../../../store/root-reducer';
import { getIsRequestPending } from '../../../utils/get-request-status';
import DraggableWrapper from '../../draggable-wrapper';
import type {
  CallViaOption,
  CountryOption,
  DialerPhoneNumberOption,
  DialerPopupProps,
  ProspectOption,
} from '../types';
import { NetworkLevel } from '../../../svg/network-strength-bars';
import DialerFooter from './dialer-footer';
import DialerHeader from './dialer-header';
import DialerKeypad from './dialer-keypad';
import DialerProspect from './dialer-prospect';
import DialerRinging from './dialer-ringing';
import {
  countryByISOCodeMap,
  countryCodePhoneInputPickerData,
} from '../../country-code-phone-input-picker/helper';

// ** css imports
import '../_dialer.scss';
import PhoneNumberPickerSheet from './phone-numbers-picker-sheet';
import {
  resetDialerControlsState,
  resetDropVoicemailTemplateRequest,
  resetEndInitiatedCallRequest,
  resetInitiateCallRequest,
  resetSelectedCallbackProspect,
  setDuringCallNote,
  setInitiatedCallFromTask,
  setOngoingCallInfo,
  resetOngoingCallInfo,
  toggleDialerControl,
} from '../../../../components/dialer/dialer-slice';
import {
  CALL_MANAGEMENT_STATE_INITIAL as callManagementStateInitial,
  isOngoingDialerCall,
} from '../constants';
import { executeOnRequestStatus } from '../../../utils';
import { RequestStatus } from '../../../enums/request-status';
import { trimSpecialCharacterFromPhoneNumber } from '../utils/helper';
import { getDialerPhoneNumberListRequest } from '../../../../components/dialer/extra-actions';
import { DialerCallSourceEnum } from '../../../../components/dialer/types/call-logs';
import DialerInCallKeypadSheet from './dialer-in-call-keypad-sheet';

// Re-export types for external use
export type { CallViaOption, CountryOption, ProspectOption };

const DialerPopup: React.FC<DialerPopupProps> = ({
  dialerPhoneNumberList = [],
  onCallClick,
  onCallDisconnect,
  onClose,
  initiateCall = false,
  initiateRinging = false,
  initiatedCallId,
  callManagementState,
  setCallManagementState,
  holdCallHandlers,
  onPreCallTestClick,
  selectedCallbackProspect,
  isCallConnected,
  setIsCallConnected,
  incomingCallProspect = null,
  isInPip = false,
  pipContainer = null,
  callConnectedAt = null,
  onVoicemailDropped,
}) => {
  const dispatch = useDispatch();
  const getDialerPhoneNumberListRequestStatus = useSelector(
    (state: RootState) =>
      state?.dialer?.getDialerPhoneNumberListRequest?.status,
  );
  const endInitiatedCallRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.endInitiatedCallRequest?.status,
  );
  const initiateCallRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.initiateCallRequest?.status,
  );
  const dropVoicemailTemplateRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.dropVoicemailTemplateRequest?.status,
  );
  const showIncomingCallStatus = useSelector(
    (state: RootState) => state?.dialer?.showIncomingCall,
  );
  const initatedCallFromTask = useSelector(
    (state: RootState) => state?.dialer?.initatedCallFromTask,
  );
  const callNetworkLevel = useSelector(
    (state: RootState) => state?.dialer?.callNetworkLevel ?? 4,
  );
  const clampedNetworkLevel = Math.max(
    0,
    Math.min(4, callNetworkLevel),
  ) as NetworkLevel;

  const endInitiatedCall = getIsRequestPending(endInitiatedCallRequestStatus);
  const isCallViaLoading = getIsRequestPending(
    getDialerPhoneNumberListRequestStatus,
  );

  const [activeTab, setActiveTab] = useState<'keypad' | 'prospect'>('keypad');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountryISOCode, setSelectedCountryISOCode] = useState<string>(
    'US',
  );
  const [selectedCallVia, setSelectedCallVia] = useState<CallViaOption | null>(
    null,
  );
  const [
    selectedProspect,
    setSelectedProspect,
  ] = useState<ProspectOption | null>(null);
  const [
    togglePhoneNumbersPickerSheet,
    setTogglePhoneNumbersPickerSheet,
  ] = useState<ProspectOption | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasReconnectingRef = useRef(false);
  const voicemailCallIdRef = useRef<number | null>(null);
  const [
    selectedCallSource,
    setSelectedCallSource,
  ] = useState<DialerCallSourceEnum>(DialerCallSourceEnum.Manual);
  const controlsState = useSelector(
    (state: RootState) => state?.dialer?.dialerControlsState,
  );
  const [inCallKeypadValue, setInCallKeypadValue] = useState('');
  const showIncomingCall = useSelector(
    (state: RootState) => state.dialer?.showIncomingCall,
  );
  const ongoingPhoneNumber = useSelector(
    (state: RootState) => state?.dialer?.ongoingPhoneNumber,
  );
  const ongoingSelectedProspect = useSelector(
    (state: RootState) => state?.dialer?.ongoingSelectedProspect,
  );
  const ongoingSelectedCountryISOCode = useSelector(
    (state: RootState) => state?.dialer?.ongoingSelectedCountryISOCode,
  );
  const callVia = useMemo(
    () =>
      dialerPhoneNumberList?.map((item) => ({
        id: item?.id,
        label: `${item?.friendlyName} ${item?.phoneNumber}`,
        value: item?.phoneNumber,
      })),
    [dialerPhoneNumberList],
  );

  const prospectName =
    `${togglePhoneNumbersPickerSheet?.firstName?.trim() || ''} ${
      togglePhoneNumbersPickerSheet?.lastName?.trim() || ''
    }`?.trim() || 'Unknown Prospect';

  const defaultCallVia = useMemo(() => {
    if (callVia?.length === 0) return null;

    return callVia[0];
  }, [callVia]);

  const selectedCountryCode = useMemo(
    () => countryByISOCodeMap.get(selectedCountryISOCode)?.code || '+1',
    [selectedCountryISOCode],
  );

  useEffect(() => {
    if (defaultCallVia) {
      setSelectedCallVia(defaultCallVia);
    }
  }, [defaultCallVia]);

  // Restore call info after PiP close/reopen (DialerPopup remount loses local state)
  useEffect(() => {
    if (
      !isOngoingDialerCall({
        callIsInprogress: callManagementState?.callIsInprogress,
        callStatus: callManagementState?.callStatus,
      })
    ) {
      return;
    }

    if (
      callManagementState?.callDirection === 'inbound' &&
      incomingCallProspect
    ) {
      setSelectedProspect((prev) => prev || { ...incomingCallProspect });
      const raw = incomingCallProspect.incomingPhoneNumber;
      if (raw) {
        const trimmed = trimSpecialCharacterFromPhoneNumber(raw) || '';
        if (trimmed) {
          setPhoneNumber(trimmed);
        }
      }
      return;
    }

    if (ongoingPhoneNumber) {
      setPhoneNumber(ongoingPhoneNumber);
      if (ongoingSelectedProspect) {
        setSelectedProspect(ongoingSelectedProspect);
      }
      if (ongoingSelectedCountryISOCode) {
        setSelectedCountryISOCode(ongoingSelectedCountryISOCode);
      }
    }
  }, [
    callManagementState?.callIsInprogress,
    callManagementState?.callStatus,
    callManagementState?.callDirection,
    incomingCallProspect,
    ongoingPhoneNumber,
    ongoingSelectedProspect,
    ongoingSelectedCountryISOCode,
  ]);

  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    setCallDuration(0);
  }, []);

  const resetCallState = useCallback(() => {
    setIsCallConnected(false);
    setPhoneNumber('');
    setSelectedProspect(null);
    setCallDuration(0);
    stopCallTimer();
    dispatch(resetOngoingCallInfo());
  }, [stopCallTimer]);

  // Handle number button click
  const handleNumberClick = useCallback((number: string) => {
    setPhoneNumber((prev) => prev + number);
  }, []);

  // Handle call button click
  const handleCall = useCallback(() => {
    const phoneNumberWithCountryCode = `${selectedCountryCode}${phoneNumber?.trim()}`;
    const checkSelectedProspectPhoneNumber = selectedProspect?.phoneNumbers?.some(
      (item) => item?.phoneNumber === phoneNumber,
    );

    if (!checkSelectedProspectPhoneNumber) {
      setSelectedProspect(null);
    }

    const validPhoneFromTaskIdCall =
      initatedCallFromTask?.allowedPhoneNumbers?.findIndex((number) =>
        number?.includes(phoneNumber?.trim()),
      ) !== -1;

    const hasSamePhoneNumber =
      selectedProspect?.phoneNumbers[0]?.phoneNumber === phoneNumber;

    const hasDigits = /\d/?.test(phoneNumber ?? '');
    if (phoneNumberWithCountryCode?.trim() && selectedCallVia && hasDigits) {
      onCallClick({
        fromDialerPhoneNumberId: selectedCallVia.id,
        toNumber: phoneNumberWithCountryCode.trim(),
        ...(validPhoneFromTaskIdCall
          ? { taskId: Number(initatedCallFromTask?.taskId) }
          : {}),
        ...(selectedProspect?.id &&
        !initatedCallFromTask?.taskId &&
        hasSamePhoneNumber
          ? { prospectId: Number(selectedProspect?.id) }
          : {}),
        callSource: selectedProspect?.callSource || selectedCallSource,
      });

      dispatch(setInitiatedCallFromTask(null));
      setIsCallConnected(true);
      dispatch(
        setOngoingCallInfo({
          phoneNumber,
          prospect: checkSelectedProspectPhoneNumber ? selectedProspect : null,
          countryISOCode: selectedCountryISOCode,
        }),
      );
    }
  }, [
    initatedCallFromTask,
    phoneNumber,
    selectedCountryCode,
    selectedCountryISOCode,
    selectedCallVia,
    selectedCallSource,
    onCallClick,
    selectedProspect,
  ]);

  // Handle prospect call
  const handleProspectCall = useCallback(
    async (
      requestedPhonenumber: DialerPhoneNumberOption & {
        callSource?: DialerCallSourceEnum;
      },
    ) => {
      setActiveTab('keypad');
      if (requestedPhonenumber) {
        if (requestedPhonenumber?.countryISOCode) {
          const countryISOCode = countryCodePhoneInputPickerData
            ?.find(
              (country) =>
                country.iso === (requestedPhonenumber?.countryISOCode || 'US'),
            )
            ?.iso?.toUpperCase();
          setSelectedCountryISOCode(countryISOCode || 'US');
        } else if (requestedPhonenumber?.callingCode) {
          const countryISOCode = countryCodePhoneInputPickerData
            ?.find(
              (country) =>
                country.code ===
                `+${Number(requestedPhonenumber?.callingCode) || 1}`,
            )
            ?.iso?.toUpperCase();
          setSelectedCountryISOCode(countryISOCode || 'US');
        }
        setSelectedCallSource(requestedPhonenumber?.callSource);
        setPhoneNumber(
          trimSpecialCharacterFromPhoneNumber(
            requestedPhonenumber?.phoneNumber || requestedPhonenumber?.number,
          ) || '',
        );
      }
    },
    [selectedCallVia?.id, onCallClick],
  );

  const handleCloseInCallKeypad = useCallback(() => {
    dispatch(toggleDialerControl('keypad'));
  }, [dispatch]);

  const handleDisconnectCall = useCallback(() => {
    if (initiatedCallId) {
      onCallDisconnect({ callId: initiatedCallId });
      if (controlsState?.keypad) {
        handleCloseInCallKeypad();
      }
    }
  }, [initiatedCallId, onCallDisconnect, controlsState.keypad]);

  const handleTogglePhoneNumbersPickerSheet = useCallback(
    (prospect: ProspectOption) => {
      setTogglePhoneNumbersPickerSheet(prospect);
    },
    [setTogglePhoneNumbersPickerSheet],
  );

  const handleClosePhoneNumbersPickerSheet = useCallback(() => {
    setTogglePhoneNumbersPickerSheet(null);
  }, [setTogglePhoneNumbersPickerSheet]);

  const sendInCallDtmf = useCallback(
    (digit: string) => {
      const call = callManagementState?.call;
      if (
        !call ||
        callManagementState?.callTwilioStatus !== 'connected' ||
        !digit
      ) {
        return;
      }
      try {
        call.sendDigits(digit);
      } catch {
        toaster.error('Could not send tone');
      }
    },
    [callManagementState?.call, callManagementState?.callTwilioStatus],
  );

  const handleClose = useCallback(() => {
    resetCallState();
    if (!showIncomingCallStatus) {
      dispatch(resetDialerControlsState());
    }
    dispatch(resetInitiateCallRequest());
    dispatch(resetEndInitiatedCallRequest());
    if (!showIncomingCall) {
      setCallManagementState(callManagementStateInitial);
    }
    dispatch(setInitiatedCallFromTask(null));
    onClose?.();
  }, [
    dispatch,
    resetCallState,
    setCallManagementState,
    onClose,
    showIncomingCall,
    showIncomingCallStatus,
  ]);

  const showInitiateCallContent = useMemo(
    () => initiateCall && !initiateRinging,
    [initiateCall, initiateRinging],
  );

  const effectiveSelectedCallVia: CallViaOption = useMemo(
    () => selectedCallVia ?? defaultCallVia ?? { id: 0, label: '', value: '' },
    [selectedCallVia, defaultCallVia],
  );

  const headerTitleText = useMemo(() => {
    if (
      callManagementState?.callTwilioStatus === 'reconnecting' &&
      (callManagementState?.callStatus === 'ringing' ||
        callManagementState?.callStatus === 'answered')
    ) {
      return 'Reconnecting...';
    }
    if (
      callManagementState?.callTwilioStatus === 'connected' &&
      callManagementState?.callStatus === 'answered'
    ) {
      return 'Connected';
    }
    if (
      !showIncomingCallStatus &&
      callManagementState?.callDirection !== 'inbound' &&
      (callManagementState?.callStatus === 'ringing' ||
        callManagementState?.callStatus === 'answered')
    ) {
      return 'Ringing...';
    }
    if (
      callManagementState?.callTwilioStatus === 'disconnected' ||
      callManagementState?.callStatus === 'completed'
    ) {
      return 'Make a Call';
    }
    if (callManagementState?.callTwilioStatus === 'error') {
      return 'Make a Call';
    }
    return 'Make a Call';
  }, [
    callManagementState?.callStatus,
    callManagementState?.callTwilioStatus,
    callManagementState?.callDirection,
    showIncomingCallStatus,
  ]);

  const initiateCallContent = useMemo(() => {
    if (activeTab === 'keypad') {
      return (
        <DialerKeypad
          phoneNumber={phoneNumber}
          onPhoneNumberChange={setPhoneNumber}
          selectedCountryISOCode={selectedCountryISOCode}
          selectedCountryCode={selectedCountryCode}
          onCountryChange={setSelectedCountryISOCode}
          selectedCallVia={effectiveSelectedCallVia}
          onCallViaChange={setSelectedCallVia}
          callVia={callVia}
          onCallClick={handleCall}
          onNumberClick={handleNumberClick}
          isCallViaLoading={isCallViaLoading}
          pipContainer={pipContainer}
        />
      );
    }
    return (
      <DialerProspect
        onProspectCall={handleProspectCall}
        onProspectSelected={setSelectedProspect}
        onTogglePhoneNumbersPickerSheet={handleTogglePhoneNumbersPickerSheet}
      />
    );
  }, [
    activeTab,
    phoneNumber,
    selectedCountryISOCode,
    selectedCountryCode,
    selectedCallVia,
    effectiveSelectedCallVia,
    callVia,
    setPhoneNumber,
    setSelectedCountryISOCode,
    setSelectedCallVia,
    handleCall,
    handleNumberClick,
    handleProspectCall,
  ]);

  useEffect(() => {
    if (activeTab !== 'prospect') {
      handleClosePhoneNumbersPickerSheet();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!callManagementState?.callIsInprogress) {
      setInCallKeypadValue('');
    }
  }, [callManagementState?.callIsInprogress]);

  // Listen when call ends from remote (e.g. other party hung up)
  useEffect(() => {
    // Skip when voicemail drop is in progress — its own handler manages cleanup
    if (
      callManagementState?.callStatus === 'completed' &&
      dropVoicemailTemplateRequestStatus !== RequestStatus.Pending &&
      dropVoicemailTemplateRequestStatus !== RequestStatus.Succeeded
    ) {
      resetCallState();
      dispatch(resetDialerControlsState());
      setCallManagementState(callManagementStateInitial);
      toaster.success('Prospect disconnected the call');
      dispatch(getDialerPhoneNumberListRequest({}));
    }
  }, [callManagementState?.callStatus]);

  // Start call duration timer when Twilio reports connected
  useEffect(() => {
    // Pause timer during reconnect — stop interval but preserve elapsed duration
    if (callManagementState?.callTwilioStatus === 'reconnecting') {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
      wasReconnectingRef.current = true;
      return undefined;
    }

    if (
      callManagementState?.callTwilioStatus === 'connected' &&
      callManagementState?.callStatus === 'answered'
    ) {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      // Only reset duration for a fresh connection, not when resuming after reconnect
      if (!wasReconnectingRef.current) {
        const elapsed = callConnectedAt
          ? Math.floor((Date.now() - callConnectedAt) / 1000)
          : 0;
        setCallDuration(elapsed);
      }
      wasReconnectingRef.current = false;
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
      return () => {
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
          callTimerRef.current = null;
        }
      };
    }

    return undefined;
  }, [callManagementState?.callTwilioStatus, callManagementState?.callStatus]);

  // Listen when call ends from PC (user clicked disconnect)
  useEffect(() => {
    executeOnRequestStatus({
      status: endInitiatedCallRequestStatus,
      onSuccess: () => {
        resetCallState();
        dispatch(resetDialerControlsState());
        setCallManagementState(callManagementStateInitial);
        dispatch(resetEndInitiatedCallRequest());
      },
      onFailed: () => {
        resetCallState();
        dispatch(resetDialerControlsState());
        setCallManagementState(callManagementStateInitial);
        dispatch(resetEndInitiatedCallRequest());
      },
    });
  }, [
    endInitiatedCallRequestStatus,
    dispatch,
    resetCallState,
    setCallManagementState,
  ]);

  // Handle initiate call API failure
  useEffect(() => {
    executeOnRequestStatus({
      status: initiateCallRequestStatus,
      onFailed: () => {
        resetCallState();
        dispatch(resetDialerControlsState());
        setCallManagementState(callManagementStateInitial);
        dispatch(resetInitiateCallRequest());
      },
      onSuccess: () => {
        dispatch(resetInitiateCallRequest());
      },
      onPending: () => {
        dispatch(setDuringCallNote(null));
      },
      onIdeal: () => {
        dispatch(setDuringCallNote(null));
      },
    });
  }, [
    initiateCallRequestStatus,
    dispatch,
    resetCallState,
    setCallManagementState,
  ]);
  useEffect(() => {
    executeOnRequestStatus({
      status: dropVoicemailTemplateRequestStatus,
      onPending: () => {
        voicemailCallIdRef.current = initiatedCallId
          ? Number(initiatedCallId)
          : null;
      },
      onSuccess: () => {
        const callId = voicemailCallIdRef.current;
        voicemailCallIdRef.current = null;

        resetCallState();
        dispatch(resetDialerControlsState());
        setCallManagementState(callManagementStateInitial);
        dispatch(resetDropVoicemailTemplateRequest());
        dispatch(getDialerPhoneNumberListRequest({}));
        toaster.success('Voicemail dropped successfully');

        if (callId && onVoicemailDropped) {
          onVoicemailDropped(callId);
        }
      },
    });
  }, [
    dropVoicemailTemplateRequestStatus,
    dispatch,
    resetCallState,
    setCallManagementState,
    initiatedCallId,
    onVoicemailDropped,
  ]);
  // Handle selected callback prospect
  useEffect(() => {
    if (selectedCallbackProspect?.length > 0) {
      setSelectedProspect((prev) => ({
        ...prev,
        callSource: selectedCallbackProspect?.[0]?.callSource,
        ...(selectedCallbackProspect?.[0]?.prospect
          ? {
              id: String(selectedCallbackProspect?.[0]?.prospect?.id),
              firstName: selectedCallbackProspect?.[0]?.prospect?.firstName,
              lastName: selectedCallbackProspect?.[0]?.prospect?.lastName,
            }
          : {}),
        phoneNumbers:
          selectedCallbackProspect?.[0]?.prospectPhoneNumberList || [
            selectedCallbackProspect?.[0]?.prospectPhone,
          ] ||
          [],
      }));
      handleProspectCall({
        number: selectedCallbackProspect?.[0]?.prospectPhone?.number || '',
        phoneNumber:
          selectedCallbackProspect?.[0]?.prospectPhone?.phoneNumber || '',
        callingCode:
          selectedCallbackProspect?.[0]?.prospectPhone?.callingCode || '',
        countryISOCode:
          selectedCallbackProspect?.[0]?.prospectPhone?.countryISOCode || '',
        callSource: selectedCallbackProspect?.[0]?.callSource,
      });
      dispatch(resetSelectedCallbackProspect());
    }
  }, [selectedCallbackProspect, dispatch, handleProspectCall]);

  useEffect(() => {
    if (incomingCallProspect) {
      setSelectedProspect(
        (prev) =>
          prev || {
            ...incomingCallProspect,
          },
      );
    }
  }, [incomingCallProspect]);

  return (
    <DraggableWrapper
      className={classNames('draggable-dialer-main-container', {
        'dialer-ringing': initiateRinging,
        'draggable-dialer-main-container--pip': isInPip,
      })}
      dragHandleSelector=".dialer-header-dots-container"
      width={300}
      height={initiateRinging ? 451 : 514}
      excludeLeftArea={true}
      excludeLeftAreaSelector=".app-sidebar"
      excludeLeftAreaPadding={20}
    >
      <div
        role="dialog"
        aria-label="Make a Call"
        className="dialer-popup-container"
      >
        {/* Header */}
        <div
          className={`dialer-header-container${
            isInPip ? ' dialer-header-container--pip' : ''
          }`}
          role="button"
          tabIndex={-1}
          aria-label="Drag to move dialer"
        >
          <DialerHeader
            title={headerTitleText}
            {...(isInPip || (initiateRinging && !initiateCall)
              ? {}
              : { onClose: handleClose })}
            isInPip={isInPip}
            pipContainer={pipContainer}
            showNetworkIcon={initiateRinging && !initiateCall}
            isCallConnected={isCallConnected}
            callDuration={callDuration}
            callManagementState={callManagementState}
            networkLevel={clampedNetworkLevel}
            onPreCallTestClick={onPreCallTestClick}
          />
        </div>

        {/* Content + Bottom Sheet (sheet opens above footer) */}
        <div className="dialer-content-wrapper">
          <div
            className={`dialer-content ${
              controlsState?.voicemail ? 'dialer-content-recording' : ''
            } ${activeTab === 'prospect' ? 'dialer-prospect-list' : ''}
            `}
          >
            {showInitiateCallContent && !isCallConnected ? (
              initiateCallContent
            ) : (
              <DialerRinging
                isCallConnected={isCallConnected}
                onCallDisconnect={handleDisconnectCall}
                initiatedCallId={initiatedCallId}
                selectedProspect={selectedProspect}
                selectedCountryCode={selectedCountryCode}
                ongoingPhoneNumber={phoneNumber}
                callManagementState={callManagementState}
                controlsState={controlsState}
                endInitiatedCall={endInitiatedCall}
                holdCallHandlers={holdCallHandlers}
                dropVoicemailTemplateRequestStatus={
                  dropVoicemailTemplateRequestStatus
                }
                inboundCallPhoneNumber={
                  incomingCallProspect?.incomingPhoneNumber
                }
              />
            )}
          </div>
          {Boolean(togglePhoneNumbersPickerSheet) && (
            <PhoneNumberPickerSheet
              isOpen={!!togglePhoneNumbersPickerSheet}
              onClose={handleClosePhoneNumbersPickerSheet}
              contactName={
                togglePhoneNumbersPickerSheet
                  ? `${(
                      togglePhoneNumbersPickerSheet?.firstName ?? ''
                    )?.trim()} ${(
                      togglePhoneNumbersPickerSheet?.lastName ?? ''
                    )?.trim()}`.trim()
                  : ''
              }
              phoneNumbers={
                togglePhoneNumbersPickerSheet
                  ? togglePhoneNumbersPickerSheet?.phoneNumbers?.map(
                      (item) => ({
                        number: item?.number,
                        label: item?.phoneType || 'Work',
                        isPrimary: item?.isPrimary || false,
                        phoneNumber: item?.phoneNumber,
                      }),
                    )
                  : []
              }
              onSelectNumber={(number) => {
                if (togglePhoneNumbersPickerSheet) {
                  setSelectedProspect(togglePhoneNumbersPickerSheet);
                  const matchedPhone = togglePhoneNumbersPickerSheet?.phoneNumbers?.find(
                    (item) =>
                      item?.phoneNumber === number || item?.number === number,
                  );
                  handleProspectCall({
                    number: matchedPhone?.number ?? number,
                    phoneNumber: matchedPhone?.phoneNumber ?? number,
                    callingCode: matchedPhone?.callingCode ?? '',
                    callSource: selectedCallSource,
                  });
                }
              }}
              inline
              prospectName={prospectName}
            />
          )}
          {callManagementState?.callIsInprogress && (
            <DialerInCallKeypadSheet
              isOpen={Boolean(controlsState?.keypad)}
              onClose={handleCloseInCallKeypad}
              inline
              value={inCallKeypadValue}
              onChange={setInCallKeypadValue}
              onSendDtmf={sendInCallDtmf}
            />
          )}
        </div>

        {/* Footer Navigation */}
        {showInitiateCallContent && !isCallConnected && (
          <DialerFooter activeTab={activeTab} onTabChange={setActiveTab} />
        )}
      </div>
    </DraggableWrapper>
  );
};

export default memo(DialerPopup);
